/**
 * <MediaDisplay> — thin media viewer router (organism, L0.1).
 *
 * Full parity with the viewing modes of legacy `MaterialDisplayModal`:
 *  - `image` → `<ImageDisplay>` (pinch/pan/rotate gallery)
 *  - `pdf`   → `<PdfDisplay>` (native PDF engine / web pdf.js)
 *  - `link`  → `<LinkDisplay>` (native WebView / web YouTube or open-in-browser)
 *
 * Owns no chrome of its own — only dispatches by `kind`.
 */
import React from "react"

import { ImageDisplay } from "../../molecules/ImageDisplay"
import { LinkDisplay } from "../../molecules/LinkDisplay"
import { PdfDisplay } from "../../molecules/PdfDisplay"

type ViewImage = { uri: string }

type MediaDisplayBase = {
    visible: boolean
    onClose: () => void
    testID?: string
}

export type MediaDisplayProps =
    | (MediaDisplayBase & {
          kind: "image"
          images: ViewImage[]
          initialIndex?: number
      })
    | (MediaDisplayBase & {
          kind: "pdf"
          uri: string
          /** Le PDF ne s'est pas chargé (`<PdfDisplay>`). */
          onError?: (cause: unknown) => void
      })
    | (MediaDisplayBase & {
          kind: "link"
          url: string
      })

export function MediaDisplay(props: MediaDisplayProps) {
    const { visible, onClose, testID } = props

    if (props.kind === "image") {
        return (
            <ImageDisplay
                visible={visible}
                images={props.images}
                initialIndex={props.initialIndex}
                onClose={onClose}
                testID={testID}
            />
        )
    }

    if (props.kind === "pdf") {
        return (
            <PdfDisplay visible={visible} uri={props.uri} onClose={onClose} onError={props.onError} testID={testID} />
        )
    }

    return <LinkDisplay visible={visible} url={props.url} onClose={onClose} testID={testID} />
}
